import moment from 'moment';
import {useCallback, useEffect, useState} from 'react';

import useFilters from '@/hooks/useFilters';
import {transactionService} from '@/services/transactionService';
import type {OffsetPageInfo} from '@/types';
import {logger} from '@/utils/logger';

import type {
  AfriexProcessorTransaction,
  AfriexProcessorTxParams,
  ProcessorTxListProps,
} from './types';
import {AfriexPaymentProcessors, AfriexProcessorPaymentTypes} from './types';

const useProcessorTransactionList = (): ProcessorTxListProps => {
  // Updated initial filter with type DEPOSIT and today's date range in UTC
  const initialFilter = {
    processor: AfriexPaymentProcessors.TRICE,
    type: AfriexProcessorPaymentTypes.DEPOSIT, // Set the initial type to DEPOSIT
    offset: 0,
    fromDate: moment()
      .subtract(1, 'months')
      .startOf('day')
      .format('YYYY-MM-DDTHH:mm:ss.SSS'),
    toDate: moment().endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
  };
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [txList, setTxList] = useState<AfriexProcessorTransaction[]>([]);
  const [paginationInfo, setPaginationInfo] = useState<OffsetPageInfo>({
    offset: 0,
    limit: 10,
    nextOffset: 10,
    hasMore: false,
    isEmpty: false,
  });
  const [error, setError] = useState<unknown>(null);
  const [currentFilters, setCurrentFilters] =
    useState<AfriexProcessorTxParams>(initialFilter);
  const [filtersToApply, setFiltersToApply] =
    useState<AfriexProcessorTxParams>(initialFilter);
  const [filterCount, setFilterCount] = useState(
    Object.values(initialFilter).filter((val) => val !== undefined).length,
  );
  const {applyFilters} = useFilters(setCurrentFilters);

  const fetchProcessorTransactions = useCallback(
    async (params?: AfriexProcessorTxParams) => {
      try {
        setError(null);
        setIsLoading(true);

        const res = await transactionService.getProcessorTransactions({
          ...params,
          ...currentFilters,
        });
        setTxList(res.data);
        setPaginationInfo(res.pageInfo);
      } catch (error) {
        logger.error(error);
        setError(error);
      } finally {
        setIsLoading(false);
      }
    },
    [currentFilters],
  );

  useEffect(() => {
    // `fetchProcessorTransactions` try/catches into `logger.error` + `setError`,
    // so this only fires if that catch block itself throws. Reported the same
    // way the fetcher reports, so a rejection cannot vanish silently.
    fetchProcessorTransactions().catch(logger.error);
  }, [fetchProcessorTransactions]);

  // Load next page of data
  const loadNextPage = useCallback(() => {
    if (paginationInfo.hasMore && !isLoading) {
      setCurrentFilters((prev) => ({
        ...prev,
        offset: paginationInfo.nextOffset,
      }));
    }
  }, [paginationInfo, isLoading]);

  // Load previous page of data
  const loadPreviousPage = useCallback(() => {
    if (paginationInfo.offset > 0 && !isLoading) {
      const prevOffset = Math.max(
        0,
        paginationInfo.offset - paginationInfo.limit,
      );
      setCurrentFilters((prev) => ({
        ...prev,
        offset: prevOffset,
      }));
    }
  }, [paginationInfo, isLoading]);

  // Reset pagination (go to first page)
  const resetPagination = useCallback(() => {
    setCurrentFilters((prev) => ({
      ...prev,
      offset: 0,
    }));
  }, []);

  // Handle change for user manipulation
  const selectCurrentFilters = useCallback(
    (
      name: string,
      value: AfriexPaymentProcessors | string | number | Date | undefined,
    ) => {
      setCurrentFilters((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        // Only count non-undefined values
        const filteredCount = Object.entries(updatedFilters).filter(
          ([_, val]) => val !== undefined,
        ).length;
        setFilterCount(filteredCount);
        return updatedFilters;
      });
      // Reset to first page when filters change
      resetPagination();
    },
    [resetPagination],
  );

  const handleApplyFilters = useCallback(() => {
    const updatedFilters = {
      ...filtersToApply,
      offset: 0,
    };
    // No pagination reset here, so there is nothing to gate -- but the panel
    // still needs to know whether the apply succeeded before it closes.
    return applyFilters(updatedFilters);
  }, [applyFilters, filtersToApply]);

  const selectAppliedFilters = useCallback(
    (
      name: string,
      value: AfriexPaymentProcessors | string | number | Date | undefined,
    ) => {
      setFiltersToApply((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        // Only count non-undefined values
        const filteredCount = Object.entries(updatedFilters).filter(
          ([_, val]) => val !== undefined,
        ).length;
        setFilterCount(filteredCount);
        return updatedFilters;
      });
    },
    [],
  );

  const clearFilters = () => {
    setCurrentFilters(initialFilter);
    setFiltersToApply(initialFilter);
    setFilterCount(
      Object.values(initialFilter).filter((val) => val !== undefined).length,
    );
    resetPagination();
  };

  const handleOffsetChange = async (newOffset: number, limit: number) => {
    // Handle direct page jumps
    // If the difference is more than one page, it's a direct page jump
    const isLargeJumpForward =
      newOffset > paginationInfo.offset + paginationInfo.limit;
    const isLargeJumpBackward =
      newOffset < paginationInfo.offset - paginationInfo.limit;

    if (isLargeJumpForward || isLargeJumpBackward) {
      // Directly set the new offset for page jumps
      setCurrentFilters((prev) => ({
        ...prev,
        offset: newOffset,
        limit: limit,
      }));
    }
    // Standard navigation for adjacent pages
    else if (newOffset > paginationInfo.offset) {
      loadNextPage();
    } else if (newOffset < paginationInfo.offset) {
      loadPreviousPage();
    } else if (newOffset === 0) {
      resetPagination();
    } else {
      // Fallback for any other case
      await fetchProcessorTransactions({
        ...currentFilters,
        offset: newOffset,
        limit: limit,
      });
    }
  };

  return {
    isLoading,
    txList,
    pageInfo: paginationInfo,
    error,
    currentFilters,
    filtersToApply,
    filterCount,
    fetchTransactionList: fetchProcessorTransactions,
    selectCurrentFilters,
    handleApplyFilters,
    selectAppliedFilters,
    clearFilters,
    handleOffsetChange,
  };
};

export default useProcessorTransactionList;
